import React, { useEffect, useState } from "react";
import "./RecordsAccounting.css";

// Formular independent: panoul stang (context si factura) ramane in ContaAplicata.
const DEFAULT_FIELDS = [
  { key: "supplier", label: "Denumire furnizor", type: "text", required: true },
  { key: "number", label: "Numar factura", type: "text", required: true },
  { key: "date", label: "Data document", type: "date", required: true },
  { key: "debit", label: "Cont debit", type: "account", required: true },
  { key: "credit", label: "Cont credit", type: "account", required: true },
  { key: "amount", label: "Valoare fara TVA", type: "number", required: true },
  { key: "vatRate", label: "Procent TVA", type: "select", required: true,
    options: [{value:"neplatitor",label:"Neplatitor de TVA"},{value:"0",label:"0%"},{value:"11",label:"11%"},{value:"21",label:"21%"}] },
  { key: "vat", label: "Valoare TVA", type: "number", required: true }
];
const DEFAULT_ACCOUNTS = [
  {value:"301",label:"301 - Materii prime"},
  {value:"302",label:"302 - Materiale consumabile"},
  {value:"371",label:"371 - Marfuri"},
  {value:"401",label:"401 - Furnizori"},
  {value:"4426",label:"4426 - TVA deductibila"},
  {value:"4427",label:"4427 - TVA colectata"}
];
const money = n => new Intl.NumberFormat("ro-RO",{style:"currency",currency:"RON"}).format(n || 0);
const initialForm = fields => {
  const values = Object.fromEntries(fields.map(f => [f.key, f.defaultValue ?? ""]));
  if (values.vatRate === "neplatitor") values.vat = "0";
  return values;
};
const cents = n => Math.round(Number(n) * 100) / 100;

export default function RecordsAccounting({
  step, lesson, invoice,
  onSaved,
  verifyUrl = "/api/accounting/verify"
}) {
  const config = step?.right?.form || {};
  const fields = config.fields?.length ? config.fields : DEFAULT_FIELDS;
  const accounts = config.accounts?.length ? config.accounts : DEFAULT_ACCOUNTS;
  const [form,setForm] = useState(() => initialForm(fields));
  const [message,setMessage] = useState("");
  const [saving,setSaving] = useState(false);
  const [entries,setEntries] = useState([]);
  const [documentValues,setDocumentValues] = useState(null);

  // Schimbarea pasului reseteaza formularul; datele facturii nu sunt precompletate.
  useEffect(() => {
    setForm(initialForm(fields));
    setMessage("");
    setEntries([]);
    setDocumentValues(null);
  }, [step?.id, lesson?.id]);

  const update = (key,value) => {
    setForm(prev => {
      const next = {...prev,[key]:value};
      if (next.vatRate === "neplatitor") {
        // Fara TVA: valoarea TVA nu se trimite ca suma calculata.
        next.vat = "0";
      } else if ((key === "amount" || key === "vatRate") &&
                 next.amount !== "" && next.vatRate !== "") {
        next.vat = String(cents(Number(next.amount) * Number(next.vatRate) / 100));
      } else if (key === "vatRate" && value === "") {
        next.vat = "";
      }
      return next;
    });
    setMessage("");
  };

  // Documentul se pastreaza; se golesc numai campurile inregistrarii.
  const addEntry = () => {
    const amount = Number(form.amount);
    const vat = form.vatRate === "neplatitor" ? 0 : Number(form.vat);
    if (!form.supplier.trim() || !form.number.trim() || !form.date || !form.vatRate) {
      setMessage("Completeaza mai intai datele facturii si regimul TVA.");
      return;
    }
    if (!form.debit || !form.credit || form.debit === form.credit ||
        form.amount === "" || !Number.isFinite(amount) || amount <= 0 ||
        (form.vatRate !== "neplatitor" &&
          (form.vat === "" || !Number.isFinite(vat) || vat < 0))) {
      setMessage("Completeaza corect conturile si valorile inregistrarii.");
      return;
    }
    if (entries.length === 0) {
      setDocumentValues({
        supplier: form.supplier.trim(), number: form.number.trim(),
        date: form.date, vatRate: form.vatRate,
        amount, vat
      });
    }
    setEntries(old => [...old, {
      id: old.length ? Math.max(...old.map(e => e.id)) + 1 : 1,
      debit: form.debit, credit: form.credit,
      amount, vat
    }]);
    setForm(old => ({...old, debit:"", credit:"", amount:"", vat:
      old.vatRate === "neplatitor" ? "0" : ""}));
    setMessage("");
  };

  const removeEntry = id => {
    setEntries(old => {
      const remaining = old.filter(entry => entry.id !== id);
      if (remaining.length === 0) setDocumentValues(null);
      return remaining;
    });
    setMessage("");
  };

  const renderField = field => {
    const id = `ra-${field.key}`;
    const value = form[field.key] ?? "";
    const options = field.type === "account"
      ? accounts
      : field.key === "vatRate"
        ? [
            { value: "neplatitor", label: "Neplatitor de TVA" },
            ...(field.options || []).filter(o => String(o.value) !== "neplatitor")
          ]
        : (field.options || []);
    return <div className="ra-field" key={field.key}>
      <label htmlFor={id}>
        {field.key === "amount" && form.vatRate === "neplatitor"
          ? "Valoare" : field.label}
      </label>
      {(field.type === "account" || field.type === "select")
        ? <select id={id} value={value} required={field.required}
            disabled={entries.length > 0 && field.key === "vatRate"}
            onChange={e=>update(field.key,e.target.value)}>
            <option value="">Selecteaza</option>
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        : <input id={id} type={field.type === "number" ? "number" : (field.type || "text")}
            step={field.type === "number" ? "0.01" : undefined}
            min={field.type === "number" ? "0" : undefined}
            required={field.required}
            disabled={entries.length > 0 && ["supplier","number","date"].includes(field.key)}
            placeholder={field.placeholder || ""}
            value={value} onChange={e=>update(field.key,e.target.value)}/>}
    </div>;
  };
  const findField = key => fields.find(f=>f.key===key);
  const renderRow = keys => <div className="ra-form-grid">
    {keys.map(key => findField(key) && renderField(findField(key)))}
  </div>;
  const known = ["supplier","number","date","debit","credit","amount","vatRate","vat"];
  const extraFields = fields.filter(f=>!known.includes(f.key));
  const total = cents((Number(form.amount)||0)+(Number(form.vat)||0));

  const save = async event => {
    event.preventDefault();
    if (entries.length === 0) {
      setMessage("Adauga cel putin o inregistrare inainte de verificare.");
      return;
    }
    if (!step?.id || !lesson?.id) {
      setMessage("Lipsesc identificatorii lectiei sau exercitiului.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      // Contract API: backend-ul verifica documentul si toate pozitiile contabile.
      // Nu trimitem niciodata step.validation catre browser.
      const response = await fetch(verifyUrl, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        credentials: "include",
        body: JSON.stringify({
          lessonId: lesson.id,
          stepId: step.id,
          answer: {
            ...documentValues,
            entries: entries.map(({debit,credit,amount}) => ({debit,credit,amount}))
          }
        })
      });
      if (!response.ok) throw new Error("Verificarea nu a putut fi efectuata.");
      const result = await response.json();
      setMessage(result.feedback || (result.correct ? "Corect!" : "Verifica inregistrarile."));
      if (result.saved) onSaved?.(result);
    } catch (err) {
      setMessage(err.message || "Eroare de comunicare cu serverul.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="ra-entry-panel" aria-label="Formular inregistrare factura">
      <div className="ra-card">
     
        <form className="ra-form" onSubmit={save} noValidate>
          <div className="ra-form-grid ra-form-grid--three">
            {["supplier", "number", "date"].map(key =>
              findField(key) && renderField(findField(key))
            )}
          </div>
          {findField("vatRate") && renderField(findField("vatRate"))}
          <div className={`ra-form-grid ${
            form.vatRate === "neplatitor"
              ? "ra-form-grid--three-equal"
              : "ra-form-grid--four"
          }`}>
            {(form.vatRate === "neplatitor"
              ? ["debit", "credit", "amount"]
              : ["debit", "credit", "amount", "vat"]
            ).map(key => findField(key) && renderField(findField(key)))}
          </div>
          {extraFields.map(renderField)}
          <div className="ra-add-row">
            <button type="button" className="ra-add-button" onClick={addEntry}>
              + Adauga inregistrare
            </button>
          </div>
          {entries.length > 0 && (
            <div className="ra-entries">
              <h4>Inregistrari adaugate ({entries.length})</h4>
              <div className="ra-table-wrapper">
                <table className="ra-table">
                  <thead><tr><th>Nr.</th><th>Cont debit</th><th>Cont credit</th>
                    <th>Valoare</th><th></th></tr></thead>
                  <tbody>{entries.map((entry, index) => (
                    <tr key={entry.id}>
                      <td>{index + 1}</td><td>{entry.debit}</td><td>{entry.credit}</td>
                      <td>{money(entry.amount)}</td>
                      <td><button type="button" className="ra-remove-button"
                        onClick={() => removeEntry(entry.id)}>Sterge</button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}
          <div className="ra-total">
            <span>Total factura</span>
            <strong>{money(documentValues
              ? documentValues.amount + documentValues.vat
              : total)}</strong>
          </div>
          {message && <p className="ra-message" role="status">{message}</p>}
          <div className="ra-actions">
            <button type="button" className="ra-secondary-button"
              onClick={() => {
                setForm(initialForm(fields));
                setEntries([]);
                setDocumentValues(null);
                setMessage("");
              }}>
              Anuleaza
            </button>
            <button type="submit" disabled={saving || entries.length === 0}
              className="ra-primary-button">
              {saving ? "Se verifica..." : "Verifica"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
